import { Component, Emit, Prop, Vue, Watch } from "vue-property-decorator";
import Modal from "@/assets/common/Modal/Modal.vue";
import { toCamel } from "snake-camel";
import { Stock } from "@/types/Component";
import moment from "moment";

type StockPostModel = {
    id: string | null;
    purchase_date: string;
    inner_capacity: number;
    quantity: number;
    amount: number;
    update_type?: "ADD" | "UPDATE" | "DELETE" | "NOCHANGE";
    updated: string | null;
};

type PurchaseHistoryPostModel = {
    stocks: StockPostModel[];
};

@Component({
    components: {
        Modal,
    },
})
export default class PurchaseHistoryModal extends Vue {
    @Prop()
    private value!: boolean;

    @Prop()
    private warehouseId!: string;

    @Prop()
    private itemType!: "chemicals" | "fertilizers";

    @Prop()
    private itemId!: string;

    @Prop()
    private unitName!: string;

    @Prop()
    private itemName!: string;

    @Prop()
    private purchaseHistoryChange!: boolean;

    private editStocks: Stock[] = [];
    private originalStocks: Stock[] = [];
    private deleteStocks: Stock[] = [];
    private modalTitle = "購入履歴";
    private alertMessage = "リクエストに失敗しました。\n";
    private isLoading = false;

    get showPurchaseHistoryModal(): boolean {
        return this.value;
    }
    set showPurchaseHistoryModal(b: boolean) {
        b;
    }

    get baseUrl(): string {
        return `/admin/warehouses/${this.warehouseId}/${this.itemType}/${this.itemId}/stocks`;
    }

    init(): void {
        this.editStocks = [];
        this.originalStocks = [];
        this.deleteStocks = [];
        if (this.value) {
            this.$apiClient.doRequest("get", this.baseUrl).then((res) => {
                this.editStocks = res.stocks.map((s: any) => toCamel(s)) as Stock[];
                this.originalStocks = this.editStocks.map((stock) => ({ ...stock }));
                this.modalTitle = "購入履歴" + "(" + this.itemName + ")";
            });
        }
    }

    @Watch("value")
    whenOpen(): void {
        this.init();
    }

    addStock(): void {
        const today = new Date();
        this.editStocks.unshift({
            id: null,
            purchaseDate: moment(today).format("yyyy-MM-DD"),
            innerCapacity: 0,
            quantity: 0,
            amount: 0,
            remainingQuantity: 0,
            updateType: "ADD",
            updated: null,
        });
        this.inputChanged();
    }

    updateStock(index: number): void {
        this.inputChanged();
        if (this.editStocks[index].id) {
            this.editStocks[index] = {
                ...this.editStocks[index],
                updateType: "UPDATE",
            };
        }
    }

    getStockTotal(stock: any): string {
        const capacity = Number(stock.innerCapacity) || 0;
        const quantity = Number(stock.quantity) || 0;
        const total = capacity * quantity;
        return total.toLocaleString();
    }

    onStockBlur(event: any, stock: any, field: string, index: number) {
        const value = this.$apiClient.onInputBlur(event, stock, field, 2);
        this.$set(stock, field, value);
        this.$forceUpdate();
        this.updateStock(index);
    }

    onStockInput(event: any, stock: any, field: "innerCapacity" | "quantity", index: number) {
        this.$apiClient.sanitizeNumber(event);
        const rawValue = event.target.value.replace(/,/g, "");
        if (rawValue === "" || rawValue === ".") {
            stock[field] = null;
        } else {
            const numValue = parseFloat(rawValue);
            stock[field] = isNaN(numValue) ? null : numValue;
        }
    }

    deleteStock(index: number): void {
        this.inputChanged();
        const targetStock = this.editStocks[index];
        if (targetStock.id) {
            const originalStock = this.originalStocks.find((stock) => stock.id === targetStock.id);
            this.deleteStocks.push({
                ...(originalStock ?? targetStock),
                updateType: "DELETE",
            });
        }
        this.editStocks.splice(index, 1);
    }

    private normalizePurchaseDate(date: string | null | undefined): string {
        if (!date) return "";
        return date.includes("T") ? date.split("T")[0] : date;
    }

    private toNumberOrZero(value: number | string | null | undefined): number {
        const numValue = Number(value);
        return Number.isNaN(numValue) ? 0 : numValue;
    }

    private isStockChanged(currentStock: Stock, originalStock: Stock): boolean {
        return (
            this.normalizePurchaseDate(currentStock.purchaseDate) !== this.normalizePurchaseDate(originalStock.purchaseDate) ||
            this.toNumberOrZero(currentStock.innerCapacity) !== this.toNumberOrZero(originalStock.innerCapacity) ||
            this.toNumberOrZero(currentStock.quantity) !== this.toNumberOrZero(originalStock.quantity) ||
            this.toNumberOrZero(currentStock.amount) !== this.toNumberOrZero(originalStock.amount)
        );
    }

    private resolveUpdateType(currentStock: Stock, originalStock: Stock | undefined): Stock["updateType"] {
        if (!currentStock.id) return "ADD";
        if (!originalStock) return "ADD";
        return this.isStockChanged(currentStock, originalStock) ? "UPDATE" : "NOCHANGE";
    }

    private isPersistedUpdateType(updateType: Stock["updateType"]): updateType is NonNullable<StockPostModel["update_type"]> {
        return updateType === "ADD" || updateType === "UPDATE" || updateType === "DELETE" || updateType === "NOCHANGE";
    }

    commit(): void {
        const errflag = {
            innerCapacityNegative: false,
            quantityNegative: false,
            quantityNotInteger: false,
            amountNegative: false,
            amountNotInteger: false,
        };
        this.editStocks.forEach((s) => {
            if (s.innerCapacity.toString() !== "") {
                if (s.innerCapacity < 0) {
                    errflag.innerCapacityNegative = true;
                }
            } else {
                s.innerCapacity = 0;
            }
            if (s.quantity.toString() !== "") {
                if (s.quantity < 0) {
                    errflag.quantityNegative = true;
                } else if (!/^\d+$/.test(s.quantity.toString())) {
                    errflag.quantityNotInteger = true;
                }
            } else {
                s.quantity = 0;
            }
            if (s.amount.toString() !== "") {
                if (s.amount < 0) {
                    errflag.amountNegative = true;
                } else if (!/^\d+$/.test(s.amount.toString())) {
                    errflag.amountNotInteger = true;
                }
            } else {
                s.amount = 0;
            }
        });

        const errmsg: string[] = [];
        if (errflag.innerCapacityNegative) {
            errmsg.push("内容量に0以上を入力してください");
        }
        if (errflag.quantityNegative) {
            errmsg.push("個数に0以上を入力してください");
        }
        if (errflag.quantityNotInteger) {
            errmsg.push("個数に整数を入力してください");
        }
        if (errflag.amountNegative) {
            errmsg.push("金額に0以上を入力してください");
        }
        if (errflag.amountNotInteger) {
            errmsg.push("金額に整数を入力してください");
        }
        if (errmsg.length > 0) {
            window.alert(errmsg.join("\n"));
            return;
        }

        const originalStocksById = new Map(this.originalStocks.filter((stock) => stock.id !== null).map((stock) => [stock.id as string, stock]));

        this.editStocks = this.editStocks.map((stock) => ({
            ...stock,
            updateType: this.resolveUpdateType(stock, stock.id ? originalStocksById.get(stock.id) : undefined),
        }));

        this.deleteStocks = this.deleteStocks.map((stock) => ({
            ...stock,
            updateType: "DELETE" as const,
        }));

        const stocks = this.editStocks.concat(this.deleteStocks);
        const data: PurchaseHistoryPostModel = {
            stocks: stocks
                .filter((s) => this.isPersistedUpdateType(s.updateType))
                .map((s) => ({
                    id: s.id,
                    purchase_date: s.purchaseDate.toString(),
                    inner_capacity: s.innerCapacity,
                    quantity: s.quantity,
                    amount: s.amount,
                    update_type: s.updateType,
                    updated: s.updated,
                })),
        };
        this.isLoading = true;
        this.$apiClient
            .doRequest("put", this.baseUrl, data)
            .then((response) => {
                if (response.message) {
                    window.alert(this.alertMessage + response.message);
                    this.$toasted.error("変更に失敗しました。");
                    return;
                }
                this.save();
                this.$toasted.success("保存しました。");
            })
            .catch(() => {
                this.$toasted.error("保存に失敗しました。");
            })
            .finally(() => {
                this.$emit("submitDetail");
                this.isLoading = false;
            });
    }
    @Emit("save")
    save(): void {
        this.editStocks = [];
        this.originalStocks = [];
        this.deleteStocks = [];
        return;
    }
    @Emit("cancel")
    cancel(): void {
        this.editStocks = [];
        this.deleteStocks = [];
        this.originalStocks = [];
        return;
    }

    @Emit("inputChanged")
    inputChanged() {
        this.$emit("update:purchaseHistoryChange", true);
    }
}
